<script setup>
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import {
    CalendarIcon,
    ChevronRightIcon,
    ViewGridIcon,
    ViewListIcon,
    SortAscendingIcon,
    SortDescendingIcon,
  } from '@heroicons/vue/solid';
  import { formatDate } from '@/utils/date';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const props = defineProps({
    projects: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['select-project']);

  // Check if mobile
  const isMobile = ref(false);

  function checkMobile() {
    isMobile.value = window.innerWidth < 768; // md breakpoint
  }

  // View mode: 'grid' or 'list' - on mobile always 'list'
  const viewMode = ref('grid');

  // Update viewMode when mobile state changes
  function updateViewMode() {
    checkMobile();
    if (isMobile.value) {
      viewMode.value = 'list';
    }
  }

  onMounted(() => {
    updateViewMode();
    window.addEventListener('resize', updateViewMode);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateViewMode);
  });

  // Sorting: 'name' or 'date'
  const sortBy = ref('date');
  const sortOrder = ref('asc'); // 'asc' or 'desc'

  function toggleSort(type) {
    if (sortBy.value === type) {
      // Same sort type - toggle order
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      // Different sort type - set to asc
      sortBy.value = type;
      sortOrder.value = 'asc';
    }
  }

  // Computed: Projects with event statistics and sorting
  const projectsWithStats = computed(() => {
    const projectsData = props.projects.map((project) => {
      const eventCount = project.eventCount || 0;

      // Get date range and earliest date for sorting from API data
      // API returns DateTimeImmutable as {date: "2026-03-07 00:00:00.000000", timezone_type: 3, timezone: "..."}
      let dateRange = null;
      let earliestDate = null;
      if (project.earliestEventDate && project.latestEventDate) {
        const earliestDateStr =
          project.earliestEventDate.date || project.earliestEventDate;
        earliestDate = new Date(earliestDateStr);
        const firstDate = formatDate(project.earliestEventDate, 'DD-MM-YYYY');
        const lastDate = formatDate(project.latestEventDate, 'DD-MM-YYYY');
        dateRange =
          firstDate === lastDate ? firstDate : `${firstDate} - ${lastDate}`;
      }

      // Get assets
      const banner = project.assets?.find((a) => a.type === 'banner');
      const square = project.assets?.find((a) => a.type === 'square');

      return {
        ...project,
        eventCount,
        dateRange,
        earliestDate,
        banner,
        square,
      };
    });

    // Sort projects
    const sorted = [...projectsData].sort((a, b) => {
      if (sortBy.value === 'name') {
        // Alphabetical sorting
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder.value === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else {
        // Date sorting
        const dateA = a.earliestDate || new Date(0);
        const dateB = b.earliestDate || new Date(0);
        return sortOrder.value === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });

    return sorted;
  });

  // Helper function to generate event count text
  function getEventCountText(project) {
    const ticketEvents = project.ticketEventCount || 0;
    const productEvents = project.productEventCount || 0;

    if (ticketEvents === 0 && productEvents === 0) {
      return '';
    }

    const parts = [];

    if (ticketEvents > 0) {
      const key =
        ticketEvents === 1
          ? 'tickets.projects.performance'
          : 'tickets.projects.performances';
      parts.push(`${ticketEvents} ${t(key)}`);
    }

    if (productEvents > 0) {
      const key =
        productEvents === 1
          ? 'tickets.projects.sale'
          : 'tickets.projects.sales';
      parts.push(`${productEvents} ${t(key)}`);
    }

    return parts.join(' en ');
  }

  function selectProject(project) {
    if (project.eventCount > 0) {
      emit('select-project', project);
    }
  }
</script>

<template>
  <div>
    <!-- Header with Title, Sort Filters and View Toggle -->
    <div class="mb-4 flex items-center justify-between">
      <h2 class="hidden text-2xl font-bold text-gray-900 md:block">
        {{ $t('tickets.projects.upcoming_events') }}
      </h2>

      <div class="flex items-center gap-3">
        <!-- Sort Filters -->
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            :class="[
              sortBy === 'name'
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            ]"
            class="inline-flex items-center gap-1.5 rounded-l-lg border px-3 py-2 text-sm font-medium"
            @click="toggleSort('name')"
          >
            <span>A-Z</span>
            <SortAscendingIcon
              v-if="sortBy === 'name' && sortOrder === 'asc'"
              class="h-4 w-4"
            />
            <SortDescendingIcon
              v-else-if="sortBy === 'name' && sortOrder === 'desc'"
              class="h-4 w-4"
            />
          </button>
          <button
            type="button"
            :class="[
              sortBy === 'date'
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            ]"
            class="inline-flex items-center gap-1.5 rounded-r-lg border-b border-r border-t px-3 py-2 text-sm font-medium"
            @click="toggleSort('date')"
          >
            <CalendarIcon class="h-4 w-4" />
            <SortAscendingIcon
              v-if="sortBy === 'date' && sortOrder === 'asc'"
              class="h-4 w-4"
            />
            <SortDescendingIcon
              v-else-if="sortBy === 'date' && sortOrder === 'desc'"
              class="h-4 w-4"
            />
          </button>
        </div>

        <!-- View Toggle - Hidden on mobile -->
        <div
          v-if="!isMobile"
          class="inline-flex rounded-md shadow-sm"
          role="group"
        >
          <button
            type="button"
            :class="[
              viewMode === 'grid'
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            ]"
            class="rounded-l-lg border px-4 py-2 text-sm font-medium"
            @click="viewMode = 'grid'"
          >
            <ViewGridIcon class="h-5 w-5" />
          </button>
          <button
            type="button"
            :class="[
              viewMode === 'list'
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            ]"
            class="rounded-r-lg border-b border-r border-t px-4 py-2 text-sm font-medium"
            @click="viewMode = 'list'"
          >
            <ViewListIcon class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div
        class="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"
      ></div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="projectsWithStats.length === 0"
      class="overflow-hidden bg-white shadow sm:rounded-md"
    >
      <div class="px-4 py-12 text-center">
        <p class="text-gray-500">{{ $t('tickets.projects.empty_state') }}</p>
      </div>
    </div>

    <!-- Grid View -->
    <div
      v-else-if="viewMode === 'grid'"
      class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
    >
      <div
        v-for="project in projectsWithStats"
        :key="`${project.type}-${project.id}`"
        class="group cursor-pointer overflow-hidden rounded-lg bg-white shadow transition-shadow hover:shadow-lg"
        :class="{ 'cursor-not-allowed opacity-50': project.eventCount === 0 }"
        @click="selectProject(project)"
      >
        <!-- Project Image/Header -->
        <div class="relative w-full overflow-hidden">
          <!-- With image: aspect-video ratio -->
          <div
            v-if="project.square || project.banner"
            class="aspect-video bg-gray-200"
          >
            <img
              :src="(project.square || project.banner).url"
              :alt="project.name"
              class="h-full w-full object-cover opacity-60 transition-transform group-hover:scale-105"
            />
          </div>
          <!-- Without image: same aspect-video with subtle gradient and decorative circles -->
          <div
            v-else
            class="from-primary-50 to-primary-100 relative aspect-video overflow-hidden bg-gradient-to-br"
          >
            <!-- Decorative circles like profile -->
            <div
              class="bg-primary-200 absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-30"
            ></div>
            <div
              class="bg-primary-200 absolute -bottom-4 -left-4 h-24 w-24 rounded-full opacity-20"
            ></div>
            <div
              class="bg-primary-300 absolute right-12 top-12 h-16 w-16 rounded-full opacity-25"
            ></div>
          </div>
        </div>

        <!-- Project Info -->
        <div class="p-4">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ project.name }}
          </h3>

          <div v-if="project.dateRange" class="mt-1 text-sm text-gray-500">
            {{ project.dateRange }}
          </div>
          <div class="mt-1 text-sm text-gray-600">
            {{ getEventCountText(project) }}
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="overflow-hidden bg-white shadow sm:rounded-md">
      <ul role="list" class="divide-y divide-gray-200">
        <li
          v-for="project in projectsWithStats"
          :key="`${project.type}-${project.id}`"
        >
          <div
            class="block hover:bg-gray-50"
            :class="{
              'cursor-pointer': project.eventCount > 0,
              'cursor-not-allowed opacity-50': project.eventCount === 0,
            }"
            @click="selectProject(project)"
          >
            <div class="flex items-center px-4 py-4 sm:px-6">
              <!-- Project Image/Icon (List view) -->
              <div
                v-if="project.square || project.banner"
                class="mr-4 shrink-0"
              >
                <img
                  :src="(project.square || project.banner).url"
                  :alt="project.name"
                  class="h-16 w-16 rounded-full object-cover opacity-70"
                />
              </div>
              <div
                v-else
                class="from-primary-50 to-primary-100 relative mr-4 h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gradient-to-br"
              >
                <!-- Decorative circles -->
                <div
                  class="bg-primary-200 absolute -right-2 -top-2 h-8 w-8 rounded-full opacity-40"
                ></div>
                <div
                  class="bg-primary-200 absolute -bottom-1 -left-1 h-6 w-6 rounded-full opacity-30"
                ></div>
                <div
                  class="bg-primary-300 opacity-35 absolute right-3 top-3 h-4 w-4 rounded-full"
                ></div>
              </div>

              <!-- Project Info -->
              <div
                class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between"
              >
                <div class="truncate">
                  <div class="text-sm">
                    <p class="text-primary-600 truncate font-medium">
                      {{ project.name }}
                    </p>
                  </div>
                  <div class="mt-2 flex items-center text-sm text-gray-500">
                    <CalendarIcon
                      class="mr-1.5 h-4 w-4 shrink-0 text-gray-400"
                    />
                    <span v-if="project.dateRange">
                      {{ project.dateRange }}
                    </span>
                    <span v-else class="italic">
                      {{ $t('tickets.projects.no_events') }}
                    </span>
                    <span class="ml-4">
                      {{ getEventCountText(project) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Chevron -->
              <div v-if="project.eventCount > 0" class="ml-5 shrink-0">
                <ChevronRightIcon class="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
